import React from 'react';
import { useCitationMode, stripCitationMarkers, CITATION_MODES } from '@/lib/deepDiveRender';

const BADGE_COLOR = {
  green:  'bg-emerald-100 text-emerald-700 border-emerald-200',
  yellow: 'bg-amber-100 text-amber-700 border-amber-200',
  orange: 'bg-orange-100 text-orange-700 border-orange-200',
  blue:   'bg-sky-100 text-sky-700 border-sky-200',
  gray:   'bg-slate-100 text-slate-600 border-slate-200',
};
const CHIP_COLOR = {
  green: 'bg-emerald-600', yellow: 'bg-amber-500', orange: 'bg-orange-500',
  blue: 'bg-sky-600', gray: 'bg-slate-500',
};

// Inline [n] → clickable, hover-preview citation chips (mirrors the reviewer's
// own CitedText so behaviour is identical).
function CitedText({ text, refs }) {
  const mode = useCitationMode();
  if (text === null || text === undefined) return null;
  if (mode === CITATION_MODES.BIBLIOGRAPHY) {
    return <span className="whitespace-pre-wrap">{stripCitationMarkers(String(text))}</span>;
  }
  const byN = {};
  (refs || []).forEach((r) => { byN[r.n] = r; });
  const parts = String(text).split(/(\[\d+\])/g);
  return (
    <span>
      {parts.map((p, i) => {
        const m = p.match(/^\[(\d+)\]$/);
        if (!m) return <span key={i}>{p}</span>;
        const n = Number(m[1]);
        const ref = byN[n];
        const color = ref?.color || 'gray';
        return (
          <span key={i} className="relative inline-block group/cite align-baseline">
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); if (ref?.url) window.open(ref.url, '_blank', 'noopener'); }}
              className={`mx-0.5 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded text-[10px] font-bold text-white ${CHIP_COLOR[color]} hover:brightness-110 cursor-pointer align-middle`}
              data-testid={`cl-cite-${n}`}
              title={ref?.url || `Reference ${n}`}
            >
              {n}
            </button>
            {ref && (
              <span className="pointer-events-none absolute left-0 bottom-full mb-1 hidden group-hover/cite:block z-50 w-64 rounded-lg border border-slate-200 bg-white p-2.5 shadow-xl text-left">
                <span className={`inline-block text-[9px] font-semibold px-1.5 py-0.5 rounded border ${BADGE_COLOR[color]}`}>{ref.badge}</span>
                <span className="block text-[11px] font-semibold text-slate-800 mt-1 leading-snug">{ref.title || ref.domain || ref.url}</span>
                <span className="block text-[10px] text-slate-400 mt-0.5">{ref.domain}{ref.year ? ` · ${ref.year}` : ''}</span>
                <span className="block text-[9px] text-indigo-500 mt-1">Click to open source ↗</span>
              </span>
            )}
          </span>
        );
      })}
    </span>
  );
}

// Recursive read-only renderer that keeps every inline [n] clickable.
// NOTE: plain function (NOT a self-referencing component) — a component that
// renders itself in its own JSX sends the CRA/craco babel dev transform into
// infinite recursion ("Maximum call stack size exceeded").
function renderCitedValue(value, refs) {
  if (value === null || value === undefined || value === '') return null;
  if (typeof value === 'string' || typeof value === 'number') {
    return <CitedText text={String(value)} refs={refs} />;
  }
  if (Array.isArray(value)) {
    return (
      <ul className="list-disc pl-4 space-y-1">
        {value.map((item, i) => <li key={i}>{renderCitedValue(item, refs)}</li>)}
      </ul>
    );
  }
  if (typeof value === 'object') {
    return (
      <div className="space-y-1">
        {Object.entries(value).map(([k, v]) => (
          (v === null || v === undefined || v === '') ? null : (
            <div key={k} className="text-sm">
              <span className="text-slate-400 capitalize">{k.replace(/_/g, ' ')}: </span>
              {renderCitedValue(v, refs)}
            </div>
          )
        ))}
      </div>
    );
  }
  return null;
}

// Read-only Competitive Landscape for the reviewer. This section also appears
// in the admin + client views, so its citations MUST be visible here — else
// refs cited only here look like phantom library entries with jumping numbers.
// We render EVERY non-empty field (known keys get friendly labels, unknown
// keys are title-cased) so no inline [n] citation can ever go unrendered.
const CL_LABELS = {
  approved_therapies: 'Approved therapies',
  major_pipeline_competitors: 'Late-stage pipeline',
  late_stage_pipeline: 'Late-stage pipeline',
  similar_mechanism_competitors: 'Similar-mechanism competitors',
  similar_mechanism: 'Similar-mechanism competitors',
  competitive_strengths: 'Competitive strengths',
  competitive_risks: 'Competitive risks',
  first_in_class_potential: 'First / best-in-class potential',
  market_position_summary: 'Competitive assessment',
  competitive_assessment: 'Competitive assessment',
};
const _titleCase = (k) => k.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

export default function CompetitiveLandscapeReview({ cl = {}, refs = [] }) {
  const isEmpty = (v) =>
    v === null || v === undefined || v === '' || (Array.isArray(v) && v.length === 0);
  const buckets = Object.entries(cl || {})
    .filter(([, v]) => !isEmpty(v))
    .map(([k, v]) => [CL_LABELS[k] || _titleCase(k), v]);

  if (buckets.length === 0) {
    return <p className="text-xs text-slate-400 italic">No competitive landscape content captured for this asset.</p>;
  }
  return (
    <div className="space-y-3" data-testid="competitive-landscape-section">
      {buckets.map(([label, val]) => (
        <div key={label}>
          <div className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold mb-0.5">{label}</div>
          <div className="text-sm text-slate-700 leading-relaxed">{renderCitedValue(val, refs)}</div>
        </div>
      ))}
      <p className="text-[10px] text-slate-400 italic">Read-only — shown so every cited reference is verifiable here (this section also appears in the admin &amp; client views).</p>
    </div>
  );
}
